
"use client";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import VoiceAgentApi from "@/Apis/VoiceAgentApi";
import { romanDraft } from "@/components/VoiceAgent/romanDraft";
import { COPY, ORDER_STATUS_LABELS, UI_LANG_STORAGE_KEY } from "./copy";
import { Icon } from "./icons";
import "./VoiceAgentV2Page.scss";

const OPEN_MS = 550;

function loadStoredUiLang() {
  if (typeof window === "undefined") return "en";
  try {
    return window.localStorage.getItem(UI_LANG_STORAGE_KEY) === "ur" ? "ur" : "en";
  } catch {
    return "en";
  }
}

function errorText(err, fallback) {
  const detail = err?.response?.data?.detail ?? err?.response?.data?.message;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const joined = detail
      .map((item) => (typeof item === "string" ? item : item?.msg))
      .filter(Boolean)
      .join(", ");
    if (joined) return joined;
  }
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

function getStorefrontBase() {
  if (typeof window === "undefined") return "";
  const { protocol, hostname, host } = window.location;
  const storefrontHost = hostname.replace(/^(admin|dashboard|cms|backoffice|panel)[.-]/i, "");
  const port = host.includes(":") ? `:${host.split(":")[1]}` : "";
  return `${protocol}//${storefrontHost}${port}`.replace(/\/+$/, "");
}

function formatPrice(value) {
  return value || value === 0
    ? `PKR ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "Price on request";
}

function readCustomerOrderStatuses(data, orderNumber) {
  const rows = Array.isArray(data?.status) ? data.status : [];
  return rows
    .filter((item) => item && typeof item === "object")
    .map((item, index) => {
      const name = item.name != null ? String(item.name).trim() : "";
      const status = item.status != null ? String(item.status).trim() : "";
      const courier = item.courier_name != null ? String(item.courier_name).trim() : "";
      const tracking = item.tracking_number != null ? String(item.tracking_number).trim() : "";
      return {
        key: `${name || orderNumber}-${index}`,
        order_number: name || orderNumber,
        status: status || "Unknown",
        courier_name: courier,
        tracking_number: tracking,
      };
    })
    .filter((row) => row.order_number || row.status);
}

function readOrder(data, orderNumber, mobile) {
  const payload =
    data?.data && typeof data.data === "object" && !Array.isArray(data.data)
      ? { ...data, ...data.data }
      : data || {};
  const order = payload.order && typeof payload.order === "object" ? payload.order : payload;
  const status = order.status || order.order_status || order.fulfillment_status || order.state || "";
  const expected =
    order.expected_delivery ||
    order.expectedDelivery ||
    order.deliveryEta ||
    order.delivery_date ||
    order.eta ||
    null;
  const number =
    order.order_number || order.orderNumber || order.name || order.number || orderNumber;
  const statusText = typeof status === "string" ? status.trim() : status ? String(status) : "";
  const courier =
    order.courier_name || order.courierName || order.courier || order.tracking_company || "";
  const tracking =
    order.tracking_number || order.trackingNumber || order.tracking || "";
  const items = Array.isArray(order.items)
    ? order.items.map((item) => ({
        name: item?.name || item?.product_title || item?.title || "Item",
        variant: item?.variant || item?.variant_title || "",
        qty: item?.qty ?? item?.quantity ?? 0,
        price: item?.lineTotal ?? item?.price ?? item?.total_price ?? null,
        unitPrice: item?.unitPrice ?? item?.unit_price ?? null,
        vendor: item?.vendor || "",
        installmentPlan: item?.installmentPlan || item?.installment_plan || "",
        markupPercentage:
          item?.markupPercentage ?? item?.markup_percentage ?? null,
        image: item?.image || item?.img_url || item?.imgUrl || "",
        handle: item?.handle || "",
      }))
    : [];
  const found =
    payload.found !== false &&
    Boolean(statusText || order.order_number || order.orderNumber || order.name || items.length);
  return {
    found,
    message:
      (typeof payload.message === "string" && payload.message) ||
      (typeof payload.detail === "string" && payload.detail) ||
      null,
    order: found
      ? {
          order_number: String(number || ""),
          status: statusText || "Unknown",
          courier_name: courier ? String(courier).trim() : "",
          tracking_number: tracking ? String(tracking).trim() : "",
          expected_delivery: expected ? String(expected) : null,
          payment_status: order.paymentStatus || order.payment_status || "",
          payment_method: order.paymentMethod || order.payment_method || "",
          total: order.total ?? null,
          currency: order.currency || "PKR",
          items,
          // Only false when the API explicitly says so — missing flag is not "unverified".
          verified: order.verified === false ? false : order.verified === true ? true : null,
        }
      : null,
  };
}

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (value.includes("deliver")) return "delivered";
  if (value.includes("cancel") || value.includes("fail") || value.includes("return")) return "alert";
  if (value.includes("pending") || value.includes("process") || value.includes("dispatch")) return "progress";
  return "neutral";
}

function productPageUrl(handle) {
  const slug = String(handle || "").trim();
  if (!slug) return "";
  return `${getStorefrontBase()}/${encodeURIComponent(slug)}`;
}

/** Spoken TTS uses 1-7-0-0 / K-I-0-4; collapse those for on-screen reading. */
function formatSpokenForDisplay(text) {
  return String(text || "")
    .replace(/\b(?:[A-Za-z0-9](?:\s*-\s*[A-Za-z0-9])+)\b/g, (match) => match.replace(/[\s-]/g, ""))
    .replace(/\s{2,}/g, " ")
    .trim();
}

function ChatBubble({ message }) {
  const role = message.role === "customer" ? "customer" : "agent";
  const display = formatSpokenForDisplay(romanDraft(message.text || ""));
  const paragraphs = display
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  return (
    <div className={`vav2-bubble is-${role}${message.draft ? " is-draft" : ""}`}>
      {paragraphs.map((part, index) => (
        <p key={`${index}-${part.slice(0, 12)}`}>{part}</p>
      ))}
    </div>
  );
}

function OrderCard({ order, t, uiLang, compact = false, itemsOnly = false }) {
  const [preview, setPreview] = useState(null);

  if (!order) return null;
  const statusText = ORDER_STATUS_LABELS[uiLang][order.status] || order.status;
  const rows = itemsOnly
    ? []
    : [
        { label: t.orderLabel, value: order.order_number },
        { label: t.statusLabel, value: statusText, badge: true },
        order.courier_name ? { label: t.courierLabel, value: order.courier_name } : null,
        order.tracking_number ? { label: t.trackingLabel, value: order.tracking_number, mono: true } : null,
        order.expected_delivery ? { label: t.expectedLabel, value: order.expected_delivery } : null,
        order.payment_status ? { label: t.paymentLabel, value: order.payment_status } : null,
        order.total != null && order.total !== ""
          ? { label: t.totalLabel, value: formatPrice(order.total), strong: true }
          : null,
      ].filter(Boolean);

  if (itemsOnly && !order.items?.length) return null;

  return (
    <article className={`vav2-order-card${compact ? " is-compact" : ""}${itemsOnly ? " is-items-only" : ""}`}>
      {!compact && !itemsOnly ? (
        <header className="vav2-order-card-head">
          <Icon name="packageSearch" />
          <span>{t.orderCardHeading}</span>
          <span className={`vav2-order-badge is-${statusTone(order.status)}`}>{statusText}</span>
        </header>
      ) : null}

      {rows.length ? (
        <dl className="vav2-order-meta">
          {rows.map((row) => (
            <div className="vav2-order-meta-row" key={`${row.label}-${row.value}`}>
              <dt>{row.label}</dt>
              <dd className={row.mono ? "is-mono" : row.strong ? "is-strong" : undefined}>
                {row.badge && compact ? (
                  <span className={`vav2-order-badge is-${statusTone(order.status)}`}>{row.value}</span>
                ) : (
                  row.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {order.items?.length ? (
        <div className="vav2-order-lines">
          {!itemsOnly ? <p className="vav2-order-lines-label">{t.itemsLabel}</p> : null}
          <ul>
            {order.items.map((item, index) => {
              const qty = formatQty(item.qty);
              const label = [
                item.name,
                item.variant && item.variant !== "Default Title" ? `(${item.variant})` : "",
              ]
                .filter(Boolean)
                .join(" ");
              const href = productPageUrl(item.handle);
              return (
                <li key={`${label}-${index}`}>
                  {item.image ? (
                    <button
                      type="button"
                      className="vav2-order-line-image is-clickable"
                      onClick={() => setPreview({ src: item.image, title: label, href })}
                      aria-label={t.imagePreviewAria}
                    >
                      <img src={item.image} alt="" />
                    </button>
                  ) : (
                    <span className="vav2-order-line-image">
                      <Icon name="imageOff" />
                    </span>
                  )}
                  <span className="vav2-order-line-body">
                    <span className="vav2-order-line-title">
                      {qty ? `${qty}× ` : ""}
                      {label}
                    </span>
                    {item.vendor ? <span className="vav2-order-line-vendor">{item.vendor}</span> : null}
                    {item.price != null && item.price !== "" ? (
                      <span className="vav2-order-line-price">
                        {formatPrice(item.price)}
                        {item.unitPrice != null &&
                        Number(item.qty) > 1 &&
                        Number(item.unitPrice) !== Number(item.price)
                          ? ` · ${formatPrice(item.unitPrice)} each`
                          : ""}
                      </span>
                    ) : null}
                    {item.installmentPlan ? (
                      <span className="vav2-order-line-meta">{item.installmentPlan}</span>
                    ) : null}
                    {item.markupPercentage != null ? (
                      <span className="vav2-order-line-meta">Markup {item.markupPercentage}%</span>
                    ) : null}
                    {href ? (
                      <a
                        className="vav2-order-line-link"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t.viewProduct}
                        <Icon name="external" />
                      </a>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {!itemsOnly && order.verified === false ? (
        <p className="vav2-unverified">{t.unverifiedNote}</p>
      ) : null}

      {preview && typeof document !== "undefined"
        ? createPortal(
            <div
              className="vav2-image-preview"
              role="dialog"
              aria-modal="true"
              aria-label={t.imagePreviewAria}
              onClick={() => setPreview(null)}
            >
              <div className="vav2-image-preview-panel" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="vav2-image-preview-close"
                  onClick={() => setPreview(null)}
                  aria-label={t.closePreviewAria}
                >
                  ×
                </button>
                <img src={preview.src} alt={preview.title || ""} />
                <div className="vav2-image-preview-footer">
                  {preview.title ? <p>{preview.title}</p> : null}
                  {preview.href ? (
                    <a href={preview.href} target="_blank" rel="noopener noreferrer">
                      {t.viewProduct}
                      <Icon name="external" />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </article>
  );
}

function normalizeOrderMessage(data, orderNumber, mobile) {
  const parsed = readOrder(data, orderNumber, mobile);
  return parsed.order || null;
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return Number.isInteger(n) ? String(n) : String(n);
}

function ticketReference(data) {
  const ref =
    data?.escalation?.ticket_reference ||
    data?.ticket_number ||
    data?.ticketNumber ||
    data?.ticket_reference ||
    data?.reference ||
    data?.reference_number ||
    data?.ticket?.reference ||
    data?.ticket?.ticketNumber ||
    data?.id;
  return ref ? String(ref) : "";
}

function historyFrom(messages) {
  return messages
    .filter(
      (message) =>
        message.text &&
        !message.draft &&
        message.type !== "products" &&
        (message.role === "customer" || message.role === "agent")
    )
    .slice(-8)
    .map((message) => ({ role: message.role, text: message.text }));
}

export default function VoiceAgentV2Page({ open = false, onClose }) {
  const accountName = useSelector((state) => state?.accountSlice?.comverse_customer_name || "");
  const accountEmail = useSelector((state) => state?.accountSlice?.comverse_customer_email || "");

  const [uiLang, setUiLang] = useState(loadStoredUiLang);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("Ready to connect");
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [callFailed, setCallFailed] = useState(false);
  const [usingSampleKnowledge, setUsingSampleKnowledge] = useState(false);
  const [demoContent, setDemoContent] = useState(false);
  const [overridesRejected, setOverridesRejected] = useState(false);

  const [orderNumberDraft, setOrderNumberDraft] = useState("");
  const [mobileDraft, setMobileDraft] = useState("");
  const [orderPanel, setOrderPanel] = useState([]);
  const [orderNote, setOrderNote] = useState("");
  const [orderBusy, setOrderBusy] = useState(false);
  const [ticket, setTicket] = useState("");

  const sessionRef = useRef(null);
  const logRef = useRef(null);
  const rootRef = useRef(null);
  const sheetRef = useRef(null);
  const t = COPY[uiLang];
  const callState = connecting
    ? "connecting"
    : callFailed && !connected
      ? "error"
      : connected && /speak/i.test(status)
        ? "speaking"
        : connected
          ? "listening"
          : "idle";
  const voiceLive = callState === "listening" || callState === "speaking";

  useEffect(() => {
    try {
      window.localStorage.setItem(UI_LANG_STORAGE_KEY, uiLang);
    } catch {
      // Language still applies for this visit.
    }
  }, [uiLang]);

  // Fonts: deferred until the slide-in has finished so they don't compete with it.
  // (Better still: move this <link> into the app <head> / _document.)
  useEffect(() => {
    const timer = setTimeout(() => {
      const id = "voice-agent-v2-fonts";
      if (!document.getElementById(id)) {
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href =
          "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Noto+Nastaliq+Urdu:wght@400;600;700&display=swap";
        document.head.appendChild(link);
      }
    }, OPEN_MS + 150);
    return () => clearTimeout(timer);
  }, []);

  // Slide the sheet in/out. Runs before paint (useLayoutEffect) and only touches
  // transform on the sheet + a class on the root (scrim fades via opacity in CSS).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const sheet = sheetRef.current;
    if (!root || !sheet) return undefined;

    if (open) {
      sheet.style.transition = "none";
      sheet.style.transform = "translate3d(0, 100%, 0)";
      void sheet.offsetHeight; // commit the start state

      let frame2 = 0;
      // Two frames: let the heavy first render settle before animating.
      const frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => {
          sheet.style.transition = `transform ${OPEN_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
          sheet.style.transform = "translate3d(0, 0, 0)";
          root.classList.add("is-shown");
        });
      });
      return () => {
        cancelAnimationFrame(frame1);
        cancelAnimationFrame(frame2);
      };
    }

    sheet.style.transition = "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)";
    sheet.style.transform = "translate3d(0, 100%, 0)";
    root.classList.remove("is-shown");
    return undefined;
  }, [open]);

  // Body scroll lock, inert background, delayed focus.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`; // avoid layout shift

    const root = rootRef.current;
    const hidden = [];
    const walk = (node) => {
      if (!node || node === root) return;
      if (node.contains(root)) {
        [...node.children].forEach(walk);
        return;
      }
      if (!node.hasAttribute("inert")) {
        node.setAttribute("inert", "");
        hidden.push(node);
      }
    };
    [...document.body.children].forEach(walk);

    // Focusing an off-screen element makes the browser scroll it into view and
    // fights the transform, so wait for the slide-in and never scroll.
    const focusTimer = setTimeout(() => {
      document.getElementById("vav2-title")?.focus({ preventScroll: true });
    }, OPEN_MS + 50);

    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
      hidden.forEach((node) => node.removeAttribute("inert"));
      sessionRef.current?.end("abandoned");
      sessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Preload the voice SDK chunk after the animation, not during it.
  useEffect(() => {
    if (callState !== "idle") return undefined;
    const timer = setTimeout(() => {
      import("@/components/VoiceAgent/elevenlabs").catch(() => {});
    }, OPEN_MS + 250);
    return () => clearTimeout(timer);
  }, [callState]);

  const customer = useMemo(
    () => ({
      customerName: String(accountName || "").trim(),
      customerEmail: String(accountEmail || "").trim(),
    }),
    [accountName, accountEmail]
  );

  const handleMessage = useCallback((message) => {
    setMessages((previous) => {
      const withoutDraft = previous.filter((item) => !item.draft);
      return [...withoutDraft, message];
    });
  }, []);

  const noteAskFlags = (data) => {
    if (data?.using_sample_knowledge) setUsingSampleKnowledge(true);
    if (data?.demo_content_published) setDemoContent(true);
    const ref = ticketReference(data);
    if (ref) setTicket(ref);
    // Only attach / refresh the product order card for overview / items asks.
    if (data?.show_order_card === false) return;
    if (data?.order && typeof data.order === "object") {
      const order = normalizeOrderMessage(
        data,
        data.order_number || data.order.order_number || data.order.orderNumber,
        data.mobile
      );
      if (order) {
        setOrderPanel([{ ...order, key: order.order_number || "ask-order" }]);
      }
    }
  };

  const pushOrderCard = (data) => {
    if (data?.show_order_card === false || !data?.order) return null;
    const order = normalizeOrderMessage(
      data,
      data?.order_number || data?.order?.order_number || data?.order?.orderNumber,
      data?.mobile
    );
    if (!order) return null;
    setOrderPanel([{ ...order, key: order.order_number || "ask-order" }]);
    return {
      role: "agent",
      type: "order",
      order,
      text: t.orderCardHeading,
    };
  };

  const connect = async () => {
    if (connected || connecting) return;
    setError("");
    setCallFailed(false);
    setOverridesRejected(false);
    setMessages([]);
    setConnecting(true);
    setStatus(t.statusConnecting);
    try {
      let Session;
      try {
        Session = (await import("@/components/VoiceAgent/elevenlabs")).ElevenLabsVoiceSession;
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        Session = (await import("@/components/VoiceAgent/elevenlabs")).ElevenLabsVoiceSession;
      }
      const session = new Session({
        customer,
        onStatus: setStatus,
        onMessage: handleMessage,
        onAskResult: (data) => noteAskFlags(data),
        onError: (message) => {
          const text = typeof message === "string" ? message : t.statusError;
          if (/override/i.test(text)) setOverridesRejected(true);
          setError(text);
          setCallFailed(true);
        },
        onDisconnect: () => {
          setConnected(false);
          setConnecting(false);
        },
      });
      sessionRef.current = session;
      await session.connect();
      setConnected(true);
      setCallFailed(false);
    } catch (err) {
      const text = errorText(err, t.statusError);
      if (/override/i.test(text)) setOverridesRejected(true);
      setError(text);
      setCallFailed(true);
      setStatus(t.statusError);
    } finally {
      setConnecting(false);
    }
  };

  const endChat = async (statusValue = "ended") => {
    await sessionRef.current?.end(statusValue);
    sessionRef.current = null;
    setConnected(false);
    setCallFailed(false);
    setStatus("Ready to connect");
  };

  const submitTyped = async () => {
    const question = draft.trim();
    if (!question || busy || voiceLive) return;
    const prior = historyFrom(messages);
    setDraft("");
    setError("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "customer", text: question }]);
    try {
      const { data } = await VoiceAgentApi.askV2({
        question,
        customerName: customer.customerName,
        customerEmail: customer.customerEmail,
        history: prior,
      });
      noteAskFlags(data);
      setMessages((prev) => {
        const next = [...prev];
        if (data?.say) next.push({ role: "agent", text: data.say });
        const orderCard = pushOrderCard(data);
        if (orderCard) next.push(orderCard);
        if (data?.products?.length) {
          next.push({
            role: "agent",
            type: "products",
            query: romanDraft(data.product_query || question),
            products: data.products,
            text: `Showing ${data.products.length} product${data.products.length === 1 ? "" : "s"}`,
          });
        }
        return next;
      });
    } catch (err) {
      setError(errorText(err, t.statusError));
    } finally {
      setBusy(false);
    }
  };

  const submitOrderLookup = async () => {
    const number = orderNumberDraft.trim();
    if (!number || orderBusy) {
      if (!number) setOrderNote(t.orderMissing);
      return;
    }
    setOrderBusy(true);
    setOrderNote("");
    try {
      const { data } = await VoiceAgentApi.getCustomerOrderStatus(number);
      const items = readCustomerOrderStatuses(data, number);
      if (!items.length) {
        setOrderPanel([]);
        setOrderNote(t.orderNotFound);
        return;
      }
      setOrderPanel(items);
      setOrderNote("");
    } catch (err) {
      setOrderPanel([]);
      setOrderNote(errorText(err, t.orderNotFound));
    } finally {
      setOrderBusy(false);
    }
  };

  const statusLabel =
    callState === "connecting"
      ? t.statusConnecting
      : callState === "speaking"
        ? t.statusSpeaking
        : callState === "listening"
          ? t.statusListening
          : callState === "error"
            ? t.statusError
            : "";

  const openProduct = (product) => {
    if (!product?.handle) return;
    const url = `${getStorefrontBase()}/${encodeURIComponent(product.handle)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const closeDialog = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") closeDialog();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeDialog]);

  return (
    // className is static on purpose: the `is-shown` class is toggled via
    // classList in the layout effect, and React must not overwrite it.
    <div className="vav2" ref={rootRef} onClick={closeDialog}>
      <main
        className="vav2-main"
        id="vav2-main"
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vav2-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div className="vav2-page-head">
            <div>
              <h1 className="vav2-title" id="vav2-title" tabIndex={-1}>
                {t.heading}
              </h1>
              <p className={`vav2-tagline${t.taglineIsUrduScript ? " urdu" : ""}`}>{t.tagline}</p>
            </div>
            <div className="vav2-head-actions">
              <div className="vav2-lang" role="group" aria-label={t.langToggleAria}>
                <button
                  type="button"
                  className={`vav2-lang-btn${uiLang === "en" ? " is-active" : ""}`}
                  aria-pressed={uiLang === "en"}
                  onClick={() => setUiLang("en")}
                >
                  {t.langEnglish}
                </button>
                <button
                  type="button"
                  className={`vav2-lang-btn urdu${uiLang === "ur" ? " is-active" : ""}`}
                  aria-pressed={uiLang === "ur"}
                  onClick={() => setUiLang("ur")}
                >
                  <span className="vav2-lang-urdu">{t.langUrdu}</span>
                </button>
              </div>
              <button type="button" className="vav2-close" onClick={closeDialog} aria-label="Close">
                ×
              </button>
            </div>
          </div>
          <p className="vav2-subtitle">{t.subtitle}</p>
        </header>

        {usingSampleKnowledge ? (
          <div className="vav2-banner">
            <Icon name="alert" />
            <p>
              <strong>{t.sampleBannerStrong}</strong> {t.sampleBannerBefore} <code>{t.sampleBannerCode}</code>{" "}
              {t.sampleBannerAfter}
            </p>
          </div>
        ) : null}

        {demoContent ? (
          <div className="vav2-banner is-orange">
            <Icon name="alert" />
            <p>
              <strong>{t.demoBannerStrong}</strong> {t.demoBannerText}
            </p>
          </div>
        ) : null}

        {overridesRejected ? (
          <div className="vav2-banner is-red">
            <Icon name="alert" />
            <p>
              <strong>{t.overridesBannerStrong}</strong> {t.overridesBannerText}
            </p>
          </div>
        ) : null}

        <div className="vav2-layout">
          <section className="vav2-chat">
            <div className="vav2-chat-head">
              <button
                type="button"
                className={`vav2-voice-btn${voiceLive ? " is-live" : ""}${callState === "error" ? " is-error" : ""}`}
                onClick={voiceLive ? () => endChat("ended") : () => connect()}
                disabled={connecting}
                aria-label={voiceLive ? t.voiceEndAria : t.voiceStartAria}
              >
                {callState === "connecting" ? (
                  <Icon name="loader" className="vav2-spin" />
                ) : callState === "speaking" ? (
                  <Icon name="volume" />
                ) : voiceLive ? (
                  <Icon name="phoneOff" />
                ) : callState === "error" ? (
                  <Icon name="micOff" />
                ) : (
                  <Icon name="mic" />
                )}
                {voiceLive ? t.voiceEnd : t.voiceStart}
              </button>
              {statusLabel ? (
                <span className={`vav2-status${callState === "error" ? " is-error" : ""}`} role="status">
                  {statusLabel}
                </span>
              ) : null}
            </div>

            <div className="vav2-log" ref={logRef} role="log" aria-live="polite" aria-label={t.conversationAriaLabel}>
              {messages.length === 0 ? (
                <div className="vav2-greeting">
                  <strong>{t.greetingTitle}</strong>
                  <p>{t.greetingBody}</p>
                </div>
              ) : null}

              {messages.map((message, index) => {
                if (message.type === "order" && message.order) {
                  const order = message.order.order_number
                    ? message.order
                    : normalizeOrderMessage({ order: message.order });
                  if (!order?.items?.length) return null;
                  return (
                    <OrderCard
                      key={`order-${order.order_number || index}`}
                      order={order}
                      t={t}
                      uiLang={uiLang}
                      itemsOnly
                    />
                  );
                }

                if (message.type === "products") {
                  return (
                    <div className="vav2-products" key={`products-${index}`}>
                      <span className="vav2-products-label">
                        {t.productsFor} &quot;{romanDraft(message.query)}&quot;
                      </span>
                      {message.products.map((product) => (
                        <button
                          type="button"
                          key={product.id || product.title}
                          className="vav2-product"
                          onClick={() => openProduct(product)}
                        >
                          <span className="vav2-product-image">
                            {product.image ? (
                              <img src={product.image} alt="" />
                            ) : (
                              <Icon name="imageOff" />
                            )}
                          </span>
                          <span className="vav2-product-body">
                            <span className="vav2-product-title">{product.title}</span>
                            {product.brand ? <span className="vav2-product-brand">{product.brand}</span> : null}
                            <span className="vav2-product-meta">
                              <span className="vav2-product-price">{formatPrice(product.price)}</span>
                              {product.compareAtPrice > product.price ? (
                                <span className="vav2-product-compare">{formatPrice(product.compareAtPrice)}</span>
                              ) : null}
                              <span className={`vav2-stock${product.inStock ? " is-in" : ""}`}>
                                {product.inStock ? t.inStock : t.outOfStock}
                              </span>
                            </span>
                          </span>
                          <span className="vav2-product-open">
                            <Icon name="external" />
                          </span>
                        </button>
                      ))}
                    </div>
                  );
                }

                return <ChatBubble key={`${message.role}-${index}`} message={message} />;
              })}

              {busy ? (
                <p className="vav2-checking">
                  <Icon name="loader" />
                  {t.checkingText}
                </p>
              ) : null}
            </div>

            {error ? <p className="vav2-error">{error}</p> : null}

            <form
              className="vav2-composer"
              onSubmit={(event) => {
                event.preventDefault();
                submitTyped();
              }}
            >
              <label className="vav2-sr" htmlFor="vav2-question">
                {t.questionLabelSr}
              </label>
              <textarea
                id="vav2-question"
                className="vav2-textarea"
                rows={2}
                value={draft}
                placeholder={voiceLive ? t.questionPlaceholderCallActive : t.questionPlaceholder}
                disabled={busy || voiceLive}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitTyped();
                  }
                }}
              />
              <button
                type="submit"
                className="vav2-icon-btn"
                disabled={busy || voiceLive || !draft.trim()}
                aria-label={t.sendAria}
              >
                {busy ? <Icon name="loader" className="vav2-spin" /> : <Icon name="send" />}
              </button>
            </form>
          </section>

          <aside className="vav2-side">
            <div className="vav2-card">
              <h2 className="vav2-card-title">
                <Icon name="packageSearch" />
                {t.orderCheckHeading}
              </h2>
              <div className="vav2-fields">
                <div>
                  <label className="vav2-label" htmlFor="vav2-order">
                    {t.orderNumberLabel}
                  </label>
                  <input
                    id="vav2-order"
                    className="vav2-text"
                    value={orderNumberDraft}
                    placeholder="Enter Order Number"
                    onChange={(event) => setOrderNumberDraft(event.target.value)}
                  />
                </div>
                <div>
                  <label className="vav2-label" htmlFor="vav2-mobile">
                    {t.mobileLabel}
                  </label>
                  <input
                    id="vav2-mobile"
                    className="vav2-text"
                    value={mobileDraft}
                    placeholder="0300 1234567"
                    inputMode="tel"
                    onChange={(event) => setMobileDraft(event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="vav2-primary-btn"
                  disabled={orderBusy || !orderNumberDraft.trim()}
                  onClick={submitOrderLookup}
                >
                  {orderBusy ? t.statusCheckingButton : t.statusCheckButton}
                </button>
              </div>

              {orderPanel.length ? (
                <div className="vav2-orders">
                  {orderPanel.map((order) => (
                    <OrderCard
                      key={order.key || order.order_number}
                      order={order}
                      t={t}
                      uiLang={uiLang}
                      compact
                    />
                  ))}
                </div>
              ) : null}
              {orderNote ? <p className="vav2-note">{orderNote}</p> : null}
            </div>

            {ticket ? (
              <div className="vav2-ticket">
                <h2 className="vav2-card-title is-small">
                  <Icon name="ticket" />
                  {t.ticketHeading}
                </h2>
                <p>
                  {ticket === "sent" ? (
                    t.handoffSent
                  ) : (
                    <>
                      {t.referenceLabel} <strong>{ticket}</strong>
                    </>
                  )}
                </p>
              </div>
            ) : null}

            <div className="vav2-card is-flat">
              <h2 className="vav2-card-title is-small">
                <Icon name="shield" />
                {t.safetyHeading}
              </h2>
              <p className="vav2-body">{t.safetyBody}</p>
            </div>

            <div className="vav2-card is-flat">
              <h2 className="vav2-card-title is-small">
                <Icon name="headphones" />
                {t.capabilitiesHeading}
              </h2>
              <ul className="vav2-list">
                <li>{t.capability1}</li>
                <li>{t.capability2}</li>
                <li>{t.capability3}</li>
              </ul>
              <p className="vav2-body">{t.capabilitiesNote}</p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}